import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { FirstNameShowComponent } from '@features/first-name-show/first-name-show.component';

import { VarDirective } from '@shared/directives/var.directive';
import { listUsersQuery, PermissionType, WorkflowStep } from '@shared/services/gql.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { Workflow, WorkflowModel } from '@shared/store/workflow/workflow.store';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectModule, ReactiveFormsModule, FirstNameShowComponent, AsyncPipe, VarDirective],
  selector: 'aux-assign-month-close',
  templateUrl: './assign-month-close.component.html',
})
export class AssignMonthCloseComponent implements OnInit {
  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private workflowQuery = inject(WorkflowQuery);
  private workflowService = inject(WorkflowService);

  assignToFormControl = new FormControl<null | string>(null);
  usersList$ = new BehaviorSubject<listUsersQuery[]>([]);
  externalUserWithPermissionsList$ = this.usersList$.pipe(
    map((listUsers) =>
      listUsers.filter(
        (user) =>
          !user.email.includes('@auxili.us') &&
          user.permissions.some(
            (permission) =>
              permission.permission_type === PermissionType.PERMISSION_APPROVE_IN_MONTH &&
              permission.permission_roles === 'E'
          )
      )
    )
  );
  isAdmin = false;
  user = '';
  userHasChecklistAdminPermission = false;
  workflow!: null | Workflow;
  workflowSubscription!: Subscription;
  readonly zeroHyphen = Utils.zeroHyphen;

  ngOnInit(): void {
    this.workflowQuery
      .getWorkflowByStepType$(WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_CLOSE_MONTH)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((workflow) => {
        this.workflow = workflow;
        this.initializeWorkflow();
        this.changeDetectorRef.detectChanges();
      });
  }

  async changeAssign() {
    const selectedUser = this.usersList$
      .getValue()
      .find((user) => user.sub === this.assignToFormControl.value);
    const currentUserID = this.authQuery.getValue().sub;
    const updateLockedBy = this.usersList$.getValue().find((user) => user.sub === currentUserID);
    const currenDate = dayjs(new Date()).format('YYYY-MM-DD');

    await this.workflowService.updateWorkflowLockStatus(
      false,
      {
        ...this.workflow,
        properties: {
          ...this.workflow?.properties,
          assigned_by: this.workflow?.updated_by,
          assigned_date: currenDate,
          assigned_to: selectedUser?.sub ? selectedUser?.sub : '',
        },
        updated_by: currentUserID ? currentUserID : this.workflow?.updated_by,
        updatedAuthorFullName: updateLockedBy?.given_name + ' ' + updateLockedBy?.family_name,
      } as WorkflowModel,
      this.isAdmin,
      true
    );
  }

  constructor() {
    this.authService
      .isAuthorized$({
        permissions: [PermissionType.PERMISSION_CHECKLIST_ADMIN],
        sysAdminsOnly: false,
      })
      .pipe(takeUntilDestroyed())
      .subscribe((userHasChecklistAdminPermission: boolean) => {
        this.userHasChecklistAdminPermission = userHasChecklistAdminPermission;
        this.changeDetectorRef.markForCheck();
      });
  }

  private initializeWorkflow(): void {
    this.isAdmin = this.authQuery.isAuxAdmin();
    this.user = '';

    if (this.workflowSubscription) {
      this.workflowSubscription.unsubscribe();
    }

    this.workflowSubscription = this.workflowService.listUsers$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users) => {
        this.usersList$.next([...users]);

        const selectedUser = users.find(
          (user) =>
            !user.email.includes('@auxili.us') &&
            user?.sub === this.workflow?.properties?.assigned_to &&
            user.permissions.some(
              (permission) =>
                permission.permission_type === PermissionType.PERMISSION_APPROVE_IN_MONTH &&
                permission.permission_roles === 'E'
            )
        );

        if (selectedUser) {
          this.user = `${selectedUser?.given_name} ${selectedUser?.family_name}`;
        }

        this.assignToFormControl.setValue(selectedUser ? selectedUser.sub : null);
        this.changeDetectorRef.markForCheck();
      });
  }
}
